import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ErrorPopup from "../../utils/popup/ErrorPopup";
import ProcessingPopup from "../../utils/popup/ProcessingPopup";
import TrimContent from "../../utils/TrimContent";
import GetCookie from "../../utils/GetCookie";
import DocumentPreview from '../../utils/DocumentPreview';
import PaginationControls from "../../utils/PaginationControls";
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

const FabricTree = ({
  onSelectionChange ,
  apiBaseUrl,
  initialFabricId = null,
  initialSelectedDocIds = [],
  initialFabricName = null,
  autoExpand = false,
  filterFabricId = null,
  initialThreadId = null,
  sidebarToggle = null,
  hasExcelTools = false,
  protocolDocId = null,
  onProtocolDocChange = null,
  hideDocuments = false,   // true → collections only: no chevron, no document list (SLR → Textractor)
}) => {
  // Document selection state - now managed internally
  const [selectedFabricId, setSelectedFabricId] = useState(() => initialFabricId);
  const [selectedDocIds, setSelectedDocIds] = useState(() => initialSelectedDocIds);
  const [selectedFabricName, setSelectedFabricName] = useState(() => initialFabricName);
  
  // Document preview states - now managed internally
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocId, setPreviewDocId] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState(null);
  
  // Tree state
  const [fabricTree, setFabricTree] = useState([]);
  const [expandedFabrics, setExpandedFabrics] = useState({});
  const [expandedThreads, setExpandedThreads] = useState({});
  const [fabricThreadMap, setFabricThreadMap] = useState({});
  const [threadDocMap, setThreadDocMap] = useState({});
  
  // Pagination and search states
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [searchInput, setSearchInput] = useState(''); 
  const [search, setSearch] = useState(''); 
  const [total, setTotal] = useState(0);
  
  // Processing and error states - now managed internally
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState("");
  
  // UPDATED: Optimized included docs functionality
  const [includedDocsMap, setIncludedDocsMap] = useState({}); // Maps threadId to included doc IDs
  const [loadingFabricIncludedDocs, setLoadingFabricIncludedDocs] = useState({}); // Loading state per fabric
  const [includedDocsButtonState, setIncludedDocsButtonState] = useState({}); // Track button state per thread
  const [fabricIncludedDocsLoaded, setFabricIncludedDocsLoaded] = useState({}); // Track which fabrics have loaded included docs

  const hasResolvedTargetRef = useRef(false);
  const navigate = useNavigate();
  
  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };
  
  // Notify parent of selection changes for API calls
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedFabricId, selectedDocIds, selectedFabricName);
    }
  }, [selectedFabricId, selectedDocIds, onSelectionChange, selectedFabricName]);
  
  // Fetch fabric tree on component mount and when search parameters change
  useEffect(() => {
    fetchFabricTree();
  }, [page, size, search, filterFabricId]);

  useEffect(() => {
    if (autoExpand && selectedFabricId && fabricTree.length > 0) {
      // Auto-expand the selected fabric and its threads
      const fabricExpansions = { [selectedFabricId]: true };
      const threadExpansions = {};
      
      // Find the selected fabric and expand its threads
      const selectedFabric = fabricTree.find(f => f.id === selectedFabricId);
      if (selectedFabric) {
        if (initialThreadId) {
          const compositeKey = `${selectedFabricId}_${initialThreadId}`;
          threadExpansions[compositeKey] = true;
        } else {
          selectedFabric.threads.forEach(thread => {
            const compositeKey = `${selectedFabricId}_${thread.id}`;
            threadExpansions[compositeKey] = true;
          });
        }
        
        // Load included docs for the auto-expanded fabric
        loadFabricIncludedDocs(selectedFabricId);
      }
      
      setExpandedFabrics(prev => ({ ...prev, ...fabricExpansions }));
      setExpandedThreads(prev => ({ ...prev, ...threadExpansions }));
    }
  }, [autoExpand, selectedFabricId, fabricTree]);

  useEffect(() => {
    // If parent passes a new initialFabricId (e.g., user opened a different chat from History),
    // allow resolver to run again.
    if (initialFabricId) {
      hasResolvedTargetRef.current = false;
    }
  }, [initialFabricId]);

  // Auto-select fabric when coming from Discovery (Copilot)
  useEffect(() => {
    if (
      initialThreadId &&
      filterFabricId &&
      fabricTree.length > 0 &&
      !selectedFabricName
    ) {
      const fabric = fabricTree.find(f => f.id === filterFabricId);
      if (!fabric) return;

      const thread = fabric.threads.find(t => t.id === initialThreadId);
      if (!thread) return;

      setSelectedFabricName(fabric.name);

      if (selectedFabricId !== filterFabricId) {
        setSelectedFabricId(filterFabricId);
      }
    }
  }, [initialThreadId, filterFabricId, fabricTree, selectedFabricName, selectedFabricId]);

  // Auto-select entire thread when coming from Discovery (Copilot)
  useEffect(() => {
    if (
      initialThreadId &&
      filterFabricId &&
      fabricTree.length > 0 &&
      threadDocMap[initialThreadId] &&
      selectedDocIds.length === 0
    ) {
      const threadDocs = threadDocMap[initialThreadId] || [];

      if (threadDocs.length > 0) {
        setSelectedDocIds(threadDocs);
      }
    }
  }, [initialThreadId, filterFabricId, fabricTree, threadDocMap, selectedDocIds.length]);

  // Auto-select included documents when coming from SLR
  useEffect(() => {
    if (
      filterFabricId &&
      fabricTree.length > 0 &&
      fabricIncludedDocsLoaded[filterFabricId] &&
      selectedDocIds.length === 0 // Only auto-select if user hasn't selected anything
    ) {
      const fabric = fabricTree.find(f => f.id === filterFabricId);
      if (!fabric) return;

      const allIncludedDocIds = [];
      fabric.threads.forEach(thread => {
        const threadIncludedDocs = includedDocsMap[thread.id] || [];
        allIncludedDocIds.push(...threadIncludedDocs);
      });

      if (allIncludedDocIds.length > 0) {
        setSelectedDocIds(allIncludedDocIds);

        const newButtonState = {};
        fabric.threads.forEach(thread => {
          const threadIncludedDocs = includedDocsMap[thread.id] || [];
          if (threadIncludedDocs.length > 0) {
            newButtonState[thread.id] = 'included_selected';
          }
        });
        setIncludedDocsButtonState(newButtonState);
      }
    }
  }, [filterFabricId, fabricTree, fabricIncludedDocsLoaded, includedDocsMap]);

  // Fetch fabric tree from API with pagination and search
  const fetchFabricTree = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage("Fetching your project");
    
    try {
      const fullUrl = `${apiBaseUrl}/fabric_unstructured_data_tree`;
      const response = await axios.post(
        fullUrl,
        { 
          include_documents: true,
          page: page,
          size: size,
          search: search || undefined,
          fabric_id: filterFabricId || undefined,
          ...(initialFabricId && !hasResolvedTargetRef.current
          ? { navigate_to_fabric_id: initialFabricId }
          : {})
        },
        { headers }
      );
        
        const data = response.data;
        
        if (data.status === 'NotAuthorized') {
          authNavigate('/login', 'iam', navigate);
          return;
        }
        
        if (data.status === 'failed') {
          const errorMessage = data.message || 'Failed to load document tree';
          setIsErrorPopupVisible(true);
          setPopupErrorMessage(errorMessage);
          return;
        }

        if (
          initialFabricId &&
          !hasResolvedTargetRef.current &&
          typeof data.page_number_for_fabric === "number"
        ) {
          if (data.page_number_for_fabric !== page) {
            hasResolvedTargetRef.current = true;
            setPage(data.page_number_for_fabric); // triggers one refetch
            return;
          }
          // already on the right page
          hasResolvedTargetRef.current = true;
        }
        
        // Set the fabric tree data
        setFabricTree(data.data || []);
        setTotal(data.total || 0);
        
        // Generate thread and document maps for selection tracking
        const threadMap = {};
        const docMap = {};
        
        data.data.forEach(fabric => {
          threadMap[fabric.id] = fabric.threads.map(thread => thread.id);
          
          fabric.threads.forEach(thread => {
            docMap[thread.id] = thread.documents.map(doc => doc.id);
          });
        });
        
        setFabricThreadMap(threadMap);
        setThreadDocMap(docMap);
        
      } catch (error) {
        console.error("Error fetching project tree:", error);
        const errorMessage = error.message || 'Failed to load document tree';
        setIsErrorPopupVisible(true);
        setPopupErrorMessage(errorMessage);
      } finally {
        setIsProcessingPopupVisible(false);
        setProcessingMessage("");
      }
    };

  // NEW: Batch load included documents for all threads in a fabric
  const loadFabricIncludedDocs = async (fabricId) => {
    // Skip if already loaded or currently loading
    if (fabricIncludedDocsLoaded[fabricId] || loadingFabricIncludedDocs[fabricId]) {
      return;
    }

    const fabric = fabricTree.find(f => f.id === fabricId);
    if (!fabric || fabric.threads.length === 0) {
      return;
    }

    setLoadingFabricIncludedDocs(prev => ({ ...prev, [fabricId]: true }));

    try {
      // Create batch requests for all threads in this fabric
      const threadRequests = fabric.threads.map(thread => 
        axios.post(
          `pceapi/fabrics/slr_included_documents`,
          { 
            thread_id: thread.id,
            fabric_id: fabricId
          },
          { headers }
        ).catch(error => ({ 
          // Return error info instead of throwing
          threadId: thread.id, 
          error: error.message 
        }))
      );

      // Execute all requests in parallel
      const responses = await Promise.all(threadRequests);
      
      // Process responses and update included docs map
      const newIncludedDocs = { ...includedDocsMap };
      
      responses.forEach((response, index) => {
        const thread = fabric.threads[index];
        
        // Handle error responses
        if (response.error) {
          console.warn(`Failed to load included docs for thread ${thread.id}:`, response.error);
          return;
        }

        const data = response.data;
        
        if (data.status === 'NotAuthorized') {
          authNavigate('/login', 'iam', navigate);
          return;
        }
        
        if (data.status === 'success' && data.included_doc_ids) {
          // Get the included document IDs from the API response
          const includedDocIds = data.included_doc_ids || [];
          const threadDocs = threadDocMap[thread.id] || [];
          
          // Filter to only include docs that exist in this thread
          const validIncludedDocs = includedDocIds.filter(docId => threadDocs.includes(docId));
          
          if (validIncludedDocs.length > 0) {
            newIncludedDocs[thread.id] = validIncludedDocs;
          }
        }
      });

      // Update state with all new included docs
      setIncludedDocsMap(newIncludedDocs);
      setFabricIncludedDocsLoaded(prev => ({ ...prev, [fabricId]: true }));

    } catch (error) {
      console.error("Error batch loading included documents:", error);
      // Don't show error popup for batch loading failures
    } finally {
      setLoadingFabricIncludedDocs(prev => ({ ...prev, [fabricId]: false }));
    }
  };
  
  // Handle search input change - only updates the input value
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };
  
  // Handle search button click - this is when we actually trigger the search
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1); // Reset to first page when search changes
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  // UPDATED: Toggle expansion of a fabric in the tree and load included docs
  const toggleFabricExpansion = (fabricId) => {
    const willBeExpanded = !expandedFabrics[fabricId];
    
    setExpandedFabrics(prev => ({
      ...prev,
      [fabricId]: willBeExpanded
    }));

    // Load included docs when fabric is expanded
    if (willBeExpanded) {
      loadFabricIncludedDocs(fabricId);
    }
  };
  
  // Toggle expansion of a thread in the tree with fabric context
  const toggleThreadExpansion = (fabricId, threadId) => {
    const compositeKey = `${fabricId}_${threadId}`;
    setExpandedThreads(prev => ({
      ...prev,
      [compositeKey]: !prev[compositeKey]
    }));
  };
  
  // Check if a thread is expanded in a specific fabric
  const isThreadExpanded = (fabricId, threadId) => {
    const compositeKey = `${fabricId}_${threadId}`;
    return expandedThreads[compositeKey] || false;
  };
  
  // Handle selection of a fabric
  const handleSelectFabric = (fabricId, fabricName) => {
    if (selectedFabricId === fabricId) {

      setSelectedFabricId(null);
      setSelectedDocIds([]);
      setSelectedFabricName(null);
      setIncludedDocsButtonState({});
    } else {
      // Select the fabric and all its documents
      // Get all document IDs from this fabric
      const allDocs = [];
      const threads = fabricThreadMap[fabricId] || [];
      
      threads.forEach(threadId => {
        const docs = threadDocMap[threadId] || [];
        allDocs.push(...docs);
      });
      
      setSelectedFabricId(fabricId);
      setSelectedDocIds(allDocs);
      setSelectedFabricName(fabricName);
      setIncludedDocsButtonState({});
    }
  };
  
  // Handle selection of a thread
  const handleThreadSelection = (fabricId, threadId, checked) => {
    // Only allow selection if this thread belongs to the selected fabric
    if (selectedFabricId && fabricId !== selectedFabricId) return;
    
    const threadDocs = threadDocMap[threadId] || [];
    let newSelection = [...selectedDocIds];
    
    if (checked) {
      // Add all documents from this thread
      threadDocs.forEach(docId => {
        if (!newSelection.includes(docId)) {
          newSelection.push(docId);
        }
      });
    } else {
      // Remove all documents from this thread
      newSelection = newSelection.filter(id => !threadDocs.includes(id));
    }
    
    setSelectedDocIds(newSelection);
    setIncludedDocsButtonState(prev => ({ ...prev, [threadId]: 'normal' }));
  };
  
  // Handle selection of an individual document
  const handleDocumentSelection = (fabricId, threadId, docId, checked) => {
    // Only allow selection if this document belongs to the selected fabric
    if (selectedFabricId && fabricId !== selectedFabricId) return;
    
    let newSelection = [...selectedDocIds];
    
    if (checked) {
      newSelection.push(docId);
    } else {
      newSelection = newSelection.filter(id => id !== docId);
    }
    
    setSelectedDocIds(newSelection);
    // Reset included docs button state for this thread
    setIncludedDocsButtonState(prev => ({ ...prev, [threadId]: 'normal' }));
  };
  
  // UPDATED: Handle Select Included Docs functionality (now instant since data is pre-loaded)
  const handleSelectIncludedDocs = (fabricId, threadId) => {
    // Only allow if this thread belongs to the selected fabric
    if (selectedFabricId && fabricId !== selectedFabricId) return;
    
    const threadDocs = threadDocMap[threadId] || [];
    const currentIncludedDocs = includedDocsMap[threadId] || [];
    const currentButtonState = includedDocsButtonState[threadId] || 'normal';
    
    // If button is in "included_selected" state, clicking again selects all docs and resets
    if (currentButtonState === 'included_selected') {
      // Select all docs in the thread
      let newSelection = [...selectedDocIds];
      
      // Remove all documents from this thread first
      newSelection = newSelection.filter(id => !threadDocs.includes(id));
      
      // Add all documents from this thread
      threadDocs.forEach(docId => {
        if (!newSelection.includes(docId)) {
          newSelection.push(docId);
        }
      });
      
      setSelectedDocIds(newSelection);
      setIncludedDocsButtonState(prev => ({ ...prev, [threadId]: 'normal' }));
      return;
    }
    
    // Check if we have included docs for this thread
    if (currentIncludedDocs.length === 0) {
      setIsErrorPopupVisible(true);
      setPopupErrorMessage("No included documents found for this thread");
      return;
    }

    // Select only the included documents, unselect others from this thread
    let newSelection = [...selectedDocIds];
    
    // Remove all documents from this thread first
    newSelection = newSelection.filter(id => !threadDocs.includes(id));
    
    // Add only the included documents
    currentIncludedDocs.forEach(docId => {
      if (!newSelection.includes(docId)) {
        newSelection.push(docId);
      }
    });
    
    setSelectedDocIds(newSelection);
    setIncludedDocsButtonState(prev => ({ ...prev, [threadId]: 'included_selected' }));
  };
  
  // Check if a thread is selected (all its documents are selected)
  const isThreadSelected = (threadId) => {
    const threadDocs = threadDocMap[threadId] || [];
    return threadDocs.length > 0 && threadDocs.every(docId => selectedDocIds.includes(docId));
  };
  
  // Check if a thread is partially selected (some but not all documents are selected)
  const isThreadPartiallySelected = (threadId) => {
    const threadDocs = threadDocMap[threadId] || [];
    return threadDocs.some(docId => selectedDocIds.includes(docId)) && 
           !threadDocs.every(docId => selectedDocIds.includes(docId));
  };
  

  const shouldShowIncludedDocsButton = (fabricId, threadId) => {
    if (!selectedFabricId) return false;
    
    if (selectedFabricId !== fabricId) return false;
    
    if (loadingFabricIncludedDocs[fabricId]) return false;
    
    const includedDocs = includedDocsMap[threadId] || [];
    return includedDocs.length > 0;
  };

  const getIncludedDocsButtonContent = (threadId) => {
    const buttonState = includedDocsButtonState[threadId] || 'normal';
    const includedDocs = includedDocsMap[threadId] || [];

    if (buttonState === 'included_selected') {
      return {
        icon: "bi-check-circle",
        text: "Included Selected"
      };
    }

    return {
      icon: "bi-filter",
      text: `Select SLR Docs (${includedDocs.length})`
    };
  };
  
  // Expand all fabrics and threads
  const expandAll = () => {
    const fabricExpansions = {};
    const threadExpansions = {};
    
    fabricTree.forEach(fabric => {
      fabricExpansions[fabric.id] = true;
      
      fabric.threads.forEach(thread => {
        // Use composite key to scope thread expansion to its fabric
        const compositeKey = `${fabric.id}_${thread.id}`;
        threadExpansions[compositeKey] = true;
      });

      // Load included docs for all fabrics being expanded
      loadFabricIncludedDocs(fabric.id);
    });
    
    setExpandedFabrics(fabricExpansions);
    setExpandedThreads(threadExpansions);
  };
  
  // Collapse all fabrics and threads
  const collapseAll = () => {
    setExpandedFabrics({});
    setExpandedThreads({});
  };
  
  // Helper function to determine if checkboxes should be disabled
  const isCheckboxDisabled = (fabricId) => {
    // If no fabric is selected, all checkboxes should be disabled
    if (!selectedFabricId) return true;
    
    // If a fabric is selected, only enable checkboxes for that fabric
    return selectedFabricId !== fabricId;
  };
  
  // Handle document preview - now managed within this component
  const handlePreviewDocument = (docId, documentName) => {
    setPreviewDocId(docId);
    setPreviewDocumentName(documentName);
    setPreviewOpen(true);
  };

  // Close document preview
  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewDocId(null);
    setPreviewDocumentName(null);
  };
  
  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };
  
  return (
    <div>
      <div className="sidebar-inner ev-card-2 overflow-hidden">
        {sidebarToggle}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <p className="ev-reg-p-dark ev-bold-reg mb-0">
            My Projects <span>({total})</span>
          </p>
        </div>
        
        <div className="d-flex justify-content-between">
          <div className="mb-2 col-8 position-relative">
            <input
              type="text"
              className="form-control search-input ms-0"
              placeholder="Search Projects"
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
            />
          
            <button 
              className="ev-btn-lightblue-fill-round search-bar-button" 
              onClick={handleSearch}
            >
              <i className="bi bi-search"></i>
            </button>
          </div>
          <div className="gap2 mt-1"> 
            <button 
              className="ev-btn-lightblue-fill-round ev-bold-reg ms-2 ev-fs-12" onClick={fetchFabricTree}>
              <i className="bi bi-arrow-repeat"></i> Refresh 
              </button>
          </div>
        </div>
           
          
        <div className="tree-container mt-2 scrollable-element">
          {fabricTree.length === 0 ? (
            <div className="text-center mt-5"><p className="ev-small-p-blue">No Projects found</p></div>
          ) : (
            <div className="tree-container-inner">
              {fabricTree.map(fabric => (
                <div key={fabric.id} className="ev-card-1 ev-padding-small mb-2 ev-radius-small">
                    <div className="text-end">
                      <span className="ev-message-card2 ev-radius-med ev-btn-small">
                        {fabric.thread_count} Data Collections
                      </span>
                      <button 
                        className={`ev-btn-blue-fill-round ev-btn-small ms-2 ${selectedFabricId === fabric.id ? 'active' : ''}`} 
                        onClick={() => handleSelectFabric(fabric.id, fabric.name)}
                        disabled={selectedFabricId !== null && selectedFabricId !== fabric.id}
                      >
                        <i className="bi bi-check2-circle"></i>&nbsp;
                        {selectedFabricId === fabric.id ? "Selected" : "Select"}
                      </button>
                    </div>    
                  <div className="d-flex align-items-center avatar-tooltip mt-1" data-tooltip={fabric.name || 'None'}>
                    <button 
                      className="btn btn-sm ev-btn-xsmall me-1" 
                      onClick={() => toggleFabricExpansion(fabric.id)}
                    >
                      <i className={`ev-reg-p-dark bi ${expandedFabrics[fabric.id] ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                    </button>                        
                    <span className="ev-small-p-blue me-1 ev-bold-med">
                      <TrimContent text={fabric.name} maxLength={25} /> &nbsp;
                    </span>
                  </div>
                  {expandedFabrics[fabric.id] && (
                    <div>
                      {fabric.threads.map(thread => {
                        const buttonContent = getIncludedDocsButtonContent(thread.id);
                        const showButton = shouldShowIncludedDocsButton(fabric.id, thread.id);
                        
                        return (
                          <div key={thread.id} className="ev-card-2 mt-2 ev-radius-small ev-padding-small">
                            <div className="d-flex align-items-center flex-wrap">
                              {!hideDocuments && (
                                <button 
                                  className="btn btn-sm ev-btn-xsmall margin-right-1 flex-shrink-0" 
                                  onClick={() => toggleThreadExpansion(fabric.id, thread.id)}>
                                  <i className={`ev-reg-p-dark bi ${isThreadExpanded(fabric.id, thread.id) ? 'bi-chevron-down' : 'bi-chevron-right'}`}></i>
                                </button>
                              )}
                              
                              <div className=" d-flex align-items-center justify-content-center flex-grow-1 min-width-0 avatar-tooltip"  data-tooltip={thread.name || 'None'}>
                                <input
                                  className="form-check-input   me-2 ev-fs-12 mb-1"
                                  type="checkbox"
                                  checked={isThreadSelected(thread.id)}
                                  onChange={(e) => handleThreadSelection(fabric.id, thread.id, e.target.checked)}
                                  id={`thread-${fabric.id}-${thread.id}`}
                                  disabled={isCheckboxDisabled(fabric.id)}
                                  ref={input => {
                                    if (input) {
                                      input.indeterminate = isThreadPartiallySelected(thread.id);
                                    }
                                  }}
                                />
                                <label 
                                  className="form-check-label ev-small-p-blue ev-bold-med text-truncate flex-grow-1" 
                                  data-tooltip={thread.name || 'None'}
                                  htmlFor={`thread-${fabric.id}-${thread.id}`}
                                >
                                  <TrimContent text={thread.name} maxLength={18} />
                                </label>
                              </div>
                              
                              <span className="badge bg-warning ev-btn-small ms-1 flex-shrink-0">
                                {thread.documents.length} docs
                              </span>
                            </div>
                            
                            {/* UPDATED: Only show SLR included docs button when there are included docs, aligned to right */}
                            {showButton && (
                              <div className="d-flex justify-content-end mt-1 mb-1">
                                <button 
                                  className={`ev-btn-blue-fill-round ev-bold-reg ev-btn-xsmall text-nowrap ${includedDocsButtonState[thread.id] === 'included_selected' ? 'active' : ''}`}
                                  onClick={() => handleSelectIncludedDocs(fabric.id, thread.id)}
                                >
                                  <i className={`bi ${buttonContent.icon} me-1`}></i>
                                  {buttonContent.text}
                                </button>
                              </div>
                            )}
                            
                            {!hideDocuments && isThreadExpanded(fabric.id, thread.id) && (
                              <ul className="mt-2">
                                {thread.documents.map(doc => (
                                  <li key={doc.id} className="ev-message-card1 ev-btn-small ev-fs-12 avatar-tooltip mb-1 pe-1 d-inline-flex align-items-center flex-nowrap" data-tooltip={doc.document_name || 'None'}>
                                      <input
                                        className="form-check-input me-2 flex-shrink-0"
                                        type="checkbox"
                                        checked={selectedDocIds.includes(doc.id)}
                                        onChange={(e) => handleDocumentSelection(fabric.id, thread.id, doc.id, e.target.checked)}
                                        id={`doc-${fabric.id}-${thread.id}-${doc.id}`}
                                        disabled={isCheckboxDisabled(fabric.id)}
                                      />
                                      <label className="form-check-label d-flex align-items-center flex-nowrap overflow-hidden" htmlFor={`doc-${fabric.id}-${thread.id}-${doc.id}`}>
                                        <small className="text-truncate" style={{maxWidth: '120px', display: 'inline-block'}}>
                                          <TrimContent text={doc.document_name} maxLength={15} />
                                        </small>
                                        <button
                                          onClick={() => handlePreviewDocument(doc.id, doc.document_name)}
                                          className="ev-btn-lightblue-fill-round ev-btn-xsmall ev-bold-reg ms-1 flex-shrink-0">
                                          <i className="bi bi-eye"></i>  View
                                        </button>
                                        {hasExcelTools && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (onProtocolDocChange) {
                                                onProtocolDocChange(protocolDocId === doc.id ? null : doc.id);
                                              }
                                            }}
                                            className={`ev-btn-xsmall ev-bold-reg ms-1 flex-shrink-0 ${protocolDocId === doc.id ? 'ev-btn-green-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                                            disabled={!selectedDocIds.includes(doc.id)}
                                            title={protocolDocId === doc.id ? 'Remove as Protocol' : 'Mark as Protocol'}
                                          >
                                            <i className={`bi ${protocolDocId === doc.id ? 'bi-bookmark-fill' : 'bi-bookmark'}`}></i>
                                            {protocolDocId === doc.id ? ' Protocol' : ' Protocol'}
                                          </button>
                                        )}
                                      </label>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Pagination for sidebar */}
        <div className="d-flex justify-content-between align-items-center sidebar-inner-bottom-fix-ele ev-fs-12">
          <div>
            <button 
              className="ev-btn-light-fill ev-btn-xsmall" 
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <i className="bi bi-chevron-left"></i>
            </button>
            
            <span className="ev-xsmall-p-dark ms-2">
              Page {page} of {Math.ceil(total / size) || 1}
            </span>
            
            <button 
              className="ev-btn-light-fill ev-btn-xsmall ms-2" 
              disabled={page >= Math.ceil(total / size)}
              onClick={() => setPage(page + 1)}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
          <div className="justify-content-between align-items-center col-3 mt-1 ev-fs-12 text-end">
          <PaginationControls 
              initialSize={size}
              onSizeChange={(newSize) => {
                setSize(newSize);
                setPage(1);
              }}
            />
        </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      <DocumentPreview 
        isOpen={previewOpen}
        onClose={closePreview}
        docId={previewDocId}
        documentName={previewDocumentName}
      />

      {/* Error and Processing popups */}
      {isErrorPopupVisible && (
        <ErrorPopup onClose={closeErrorPopup} message={popupErrorMessage} />
      )}
      
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
    </div>
  );
};

export default FabricTree;